import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addScrollBar, customizeTooltip
    , customizeLegend
     , exportCSV
 } from "../../../../../../utils/chart";
 import {EXPORT_CSV_LABELS } from "../../../../../../utils/enums"; 
import css from "../../../../../../common/chartMagneticStyle.less";
import { colors } from "../../../../../../utils/colors";
import { am4core, am4charts } from "../../../../../../loaders/amchartsLoader";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { shortDisplayTime } from "../../../../../../utils/displayTime";
import { useI18n } from "../../../../../../i18n/useI18n";
import { initChartThemeSupport, createThemeChangeObserver, applyChartDarkTheme } from "../../../../../../common/Theme";


  const [HbrIconButton, HbrTooltip] = reactWrapper(["hbr-icon-button", "hbr-tooltip"]);
const ADHeatMap = (props) => {
    const { tViews } = useI18n('views');
    const chartRef = useRef(null);
    const themeObserverRef = useRef(null);
    const [chartHeight, setChartHeight] = useState(null)
    const [timeStampLabel, setTimeStampLabel] = useState();
    const legendRef = useRef(null);

    const addLegends = (chart) => {
        const legend = new am4charts.Legend();
        legend.data = [
            { name: tViews('legendAvail'), fill: am4core.color(colors.green55) },
            { name: tViews('legendUnavail'), fill: am4core.color(colors.red45) }
        ];
        customizeLegend(legend, false);
        legend.itemContainers.template.focusable = true;
        legendRef.current = legend;
        legend.parent = chart.chartContainer;
    }

    const createDownSeries = (chart) => {
        let downSeries = chart.series.push(new am4charts.ColumnSeries());
        downSeries.data = props.data;
        downSeries.showOnInit = false;
        downSeries.dataFields.dateX = "entry_ts";
        downSeries.dataFields.openDateX = "local_exit_time";
        downSeries.dataFields.categoryY = "circuit_name";
        downSeries.dataFields.message = "message";
        customizeTooltip(downSeries.tooltip);

        const columnTemplate = downSeries.columns.template;
        columnTemplate.stroke = am4core.color(colors.red45);
        columnTemplate.strokeOpacity = 1;
        columnTemplate.fill = am4core.color(colors.red45);
        columnTemplate.height = 8;
        columnTemplate.dy = -0.5;
        columnTemplate.tooltipHTML = `
            <div>
                <div>{circuit_name}</div>
                <div>{entry_ts}</div>
                <div>{message}</div>
            </div>
        `;

        return downSeries;
    }

    const createYAxis = (chart) => {
        let yAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        yAxis.dataFields.category = "circuit_name";
        yAxis.renderer.grid.template.disabled = false;
        yAxis.renderer.inversed = true;
        yAxis.renderer.minGridDistance = 30;
        yAxis.data = props.circuitList.map(c => ({ circuit_name: c.name }));
        yAxis.renderer.grid.template.stroke = am4core.color(colors.green55);
        yAxis.renderer.grid.template.adapter.add("stroke", () => am4core.color(colors.green55));
        yAxis.renderer.grid.template.strokeWidth = 10;
        yAxis.renderer.grid.template.adapter.add("strokeOpacity", () => 1);
        yAxis.renderer.grid.template.strokeOpacity = 1;

       return yAxis;
    };

    const createXAxis = (chart) => {
       let xAxis =  chart.xAxes.push(new am4charts.DateAxis());
       xAxis.dataFields.date = "entry_ts";
       xAxis.renderer.grid.template.disabled = true;
       xAxis.renderer.minGridDistance = 40;
       xAxis.baseInterval = { count: 1, timeUnit: "minutes" };
       return xAxis;
    };

    const createChart = () => {
        am4core.options.autoDispose = true;
        let chart = am4core.create(props.id, am4charts.XYChart);
        chart.invalidateRawData();
        chart.data = props.data;
        chart.maskBullets = false;
        chart.paddingRight = 40;
        let height = Math.max(props.circuitList.length * 70, 250);
        setChartHeight(height);
        return chart;
    };

    // helper inside component
    const applyLocalLegendThemeSupport = () => {
        if (!legendRef.current || legendRef.current.isDisposed?.()) return;

        // Apply only legend-related handlers/colors to this local legend
        applyChartDarkTheme({ legend: legendRef.current }, am4core, props);
    };

    useEffect(() => {
        let chart = createChart();
        chartRef.current = chart;

        createXAxis(chartRef.current);
        createYAxis(chartRef.current);
        createDownSeries(chartRef.current);
        addScrollBar(chartRef.current, false, null, {
            insertBeforeChart: true,
            labelPrefix:
                chartRef.current.yAxes.getIndex(0)?.title?.text || tViews('circuit.ctName')
        });
        addLegends(chartRef.current);

        // One-time chart theme init
        initChartThemeSupport(chartRef.current, am4core, props);

        // One-time local legend wiring
        applyLocalLegendThemeSupport();

        const refreshLocalTheme = () => {
            if (chartRef.current?.yAxes) {
                chartRef.current.yAxes.each(axis => {
                    axis.renderer.grid.template.stroke = am4core.color(colors.green55);
                    axis.renderer.grid.template.strokeWidth = 10;
                    axis.renderer.grid.template.strokeOpacity = 1;
                });
            }
            applyLocalLegendThemeSupport();
        };

        themeObserverRef.current = createThemeChangeObserver(() => {
            refreshLocalTheme();
        });

        setTimeStampLabel(shortDisplayTime(props.timePeriod?.[0]) + " - " + shortDisplayTime(props.timePeriod?.[1]));

        return () => {

            if (themeObserverRef.current) {
                themeObserverRef.current.disconnect();
                themeObserverRef.current = null;
            }

            legendRef.current = null;

            if (chartRef.current) {
                chartRef.current.dispose();
            }
        };
    }, [props.data]);

     const onDownloadCsvClick = () => {
        if (chartRef.current && props.id) {
          exportCSV(chartRef.current, EXPORT_CSV_LABELS.AD_CIRCUITS_AVAILABILITY);
        }
     };

    return (
        <div>
              <div id="usage-based-ad-download-csv">
                <HbrTooltip content={tViews('downloadCSV')}>
                    <span>
                        <HbrIconButton
                            size="20px"
                            name="file-csv"
                            data-testid="DownloadCsvBtn"
                            onClick={() => onDownloadCsvClick()}
                            arialabel={tViews('downloadChartDisplaying') + props.id.toLowerCase() + tViews('downloadCSVInfo') + timeStampLabel}
                        />
                    </span>
                </HbrTooltip> 
            </div>  
            <div
                id={props.id}
                className={`${css.chart} hbr-type-body4`}
                style={{ height: chartHeight }}
                role="region"
                aria-label={props.id}
                tabIndex={0}></div>
        </div>
    );
}

ADHeatMap.propTypes = {
    id: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    circuitList: PropTypes.array.isRequired,
    timePeriod: PropTypes.array
}

export default ADHeatMap;

